import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { Input, Button } from "antd";
import { SendOutlined, LockOutlined } from "@ant-design/icons";
import { useMedia } from "react-use";
import { device } from "../../lib/breakpoints";
import { isSandboxDeployment } from "../../helpers/constants";
import { trackActivityEvent } from "../../lib/analytics/trackActivityEvent";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

console.log(
  "[DemoChat] module init - REACT_APP_API_URL:",
  JSON.stringify(process.env.REACT_APP_API_URL ?? "(unset)"),
  "| API_BASE_URL resolved to:",
  JSON.stringify(API_BASE_URL || "(empty / offline mode)")
);

/** Abort hung demo requests so the typing indicator cannot spin forever */
const MARKETING_DEMO_FETCH_MS = 45000;

/** Must match clumse-backend MARKETING_DEMO_MAX_USER_MESSAGES */
const DEMO_USER_MESSAGE_CAP = 5;

const DEMO_LIMIT_REPLY =
  "You've reached the demo message limit (5). In a live workspace, Clumse AI scales with your traffic 24/7. Choose a plan when you're ready to go live.";

const COOKIE_SESSION_KEY = "lo_demo_sid";
const COOKIE_COUNT_KEY = "lo_demo_cnt";
/** Epoch ms when demo cookies expire; set once per demo “week” so counts don’t slide forever */
const COOKIE_UNTIL_KEY = "lo_demo_until";
/** One week - then cookies expire and the visitor gets a fresh 5-message demo */
const COOKIE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookieExpiringAt(name, value, expiresAtMs) {
  const expires = new Date(expiresAtMs).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=/; SameSite=Lax`;
}

function getDemoExpiresAt() {
  const raw = getCookie(COOKIE_UNTIL_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function getDemoSession() {
  let sid = getCookie(COOKIE_SESSION_KEY);
  let until = getDemoExpiresAt();
  const now = Date.now();

  if (!sid) {
    sid = `mlc-${now}-${Math.random().toString(36).slice(2, 10)}`;
    until = now + COOKIE_TTL_MS;
    setCookieExpiringAt(COOKIE_SESSION_KEY, sid, until);
    setCookieExpiringAt(COOKIE_UNTIL_KEY, String(until), until);
    setCookieExpiringAt(COOKIE_COUNT_KEY, "0", until);
    return sid;
  }

  /* Older cookies without until: start a full week from now */
  if (until == null) {
    until = now + COOKIE_TTL_MS;
    setCookieExpiringAt(COOKIE_UNTIL_KEY, String(until), until);
    setCookieExpiringAt(COOKIE_SESSION_KEY, sid, until);
    const cnt = getCookie(COOKIE_COUNT_KEY) || "0";
    setCookieExpiringAt(COOKIE_COUNT_KEY, cnt, until);
  }

  return sid;
}

function getDemoMessageCount() {
  return parseInt(getCookie(COOKIE_COUNT_KEY) || "0", 10);
}

function incrementDemoMessageCount() {
  const next = getDemoMessageCount() + 1;
  let until = getDemoExpiresAt();
  if (until == null) {
    until = Date.now() + COOKIE_TTL_MS;
    setCookieExpiringAt(COOKIE_UNTIL_KEY, String(until), until);
    const sid = getCookie(COOKIE_SESSION_KEY);
    if (sid) setCookieExpiringAt(COOKIE_SESSION_KEY, sid, until);
  }
  setCookieExpiringAt(COOKIE_COUNT_KEY, String(next), until);
  return next;
}

const TRANSCRIPT_STORAGE_KEY = "lo_demo_transcript_v1";

/** Clear demo cookies + transcript so testers can reset the 5-message cap without incognito. */
function clearDemoRestrictionStorage() {
  const past = new Date(0).toUTCString();
  [COOKIE_SESSION_KEY, COOKIE_COUNT_KEY, COOKIE_UNTIL_KEY].forEach((name) => {
    document.cookie = `${name}=; expires=${past}; path=/; SameSite=Lax`;
  });
  try {
    localStorage.removeItem(TRANSCRIPT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Marketing Leo demo: reset cookies/transcript; only when REACT_APP_DEPLOYMENT=sandbox */
const SHOW_DEMO_DEBUG_CLEAR = isSandboxDeployment();

function buildDemoIntroBubble(label, marketingMode) {
  const introMarketing = `Hi! I'm Clumse AI. This is a demo-only preview for ${label}. Nothing here routes to a real operator inbox. People often try site-reporter questions (maintenance, noise, access, or a quick hello) to see how triage reads. A real occupant uses your live Magic Link instead of this on-site preview. You can send up to ${DEMO_USER_MESSAGE_CAP} messages in this demo; production threads follow your plan limits.`;
  const introOffline = `Hi! I'm Clumse AI. Demo-only preview for ${label} (sample replies, or connect the API for live AI). Same idea: nothing reaches a landlord. You can send up to ${DEMO_USER_MESSAGE_CAP} messages here.`;
  return {
    from: "assistant",
    text: marketingMode ? introMarketing : introOffline,
    timestamp: new Date().toISOString(),
  };
}

function loadDemoTranscript(currentSessionId) {
  if (typeof window === "undefined" || !currentSessionId) return null;
  try {
    const raw = localStorage.getItem(TRANSCRIPT_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      !data ||
      data.sessionId !== currentSessionId ||
      !Array.isArray(data.messages) ||
      data.messages.length === 0
    ) {
      return null;
    }
    const messages = data.messages.filter(
      (m) =>
        m &&
        typeof m.text === "string" &&
        (m.from === "user" || m.from === "assistant")
    );
    if (messages.length === 0) return null;
    const mode =
      data.mode === "offline" || data.mode === "marketing" ? data.mode : null;
    return { messages, mode };
  } catch {
    return null;
  }
}

function saveDemoTranscript(sessionId, { messages, mode }) {
  if (typeof window === "undefined" || !sessionId || !messages?.length) return;
  try {
    localStorage.setItem(
      TRANSCRIPT_STORAGE_KEY,
      JSON.stringify({
        sessionId,
        messages,
        mode,
        savedAt: Date.now(),
      })
    );
  } catch (e) {
    console.warn("[MarketingLeoDemoChat] Could not save transcript:", e);
  }
}

const DEMO_DISCORD_START_KEY = "lo_home_leo_demo_discord_started";

/**
 * Discord activity webhook (via /api/public/activity): first user message in a
 * demo session → demo_started; every user message → demo_message.
 */
function trackHomeLeoDemoDiscord({
  sessionId,
  mode,
  propertyLabel,
  messageText,
  messageNumber,
}) {
  if (typeof window === "undefined" || !sessionId || !messageText?.trim()) {
    return;
  }
  const baseMeta = {
    source: "homepage_leo_demo",
    sessionId,
    mode,
    propertyLabel,
    messageNumber,
    preview: messageText.trim().slice(0, 200),
  };

  let isFirstInSession = false;
  try {
    const k = `${DEMO_DISCORD_START_KEY}_${sessionId}`;
    isFirstInSession = !sessionStorage.getItem(k);
    if (isFirstInSession) sessionStorage.setItem(k, "1");
  } catch {
    isFirstInSession = messageNumber === 1;
  }

  if (isFirstInSession) {
    trackActivityEvent("demo_started", baseMeta);
  }
  trackActivityEvent("demo_message", baseMeta);
}

/** Matches TenantChat's formatTime: ISO string to "h:mm AM/PM" */
function formatDemoTime(timestamp) {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function getOfflineLeoReply(userText, propertyLabel) {
  const label = propertyLabel.trim() || "this property";
  const t = userText.toLowerCase();

  if (
    /landlord|property\s*manager|i\s+own|my\s+tenant|message\s+(my\s+)?tenant|send\s+to\s+tenant/.test(
      t
    )
  ) {
    return `In the real product, you'd share your Magic Link from the Clumse dashboard so reporters message the workspace instead of personal phones. This box is demo-only: nothing is delivered to anyone. Try: "There's a leak under my sink" or "Who covers HVAC filters?"`;
  }
  if (/^(hi|hello|hey|good\s(morning|afternoon|evening))\b|^yo\b/.test(t)) {
    return `Hi! I'm Clumse AI for ${label}. I answer site messages here first so your operator is not the default for every drip or policy question. What do you need help with?`;
  }
  if (/drip|faucet|sink|toilet|clog|plumb|hvac|heat|ac\b|appliance/.test(t)) {
    return `I'll log this as a maintenance request for ${label}. Your operator sees it in their triage queue and can schedule a normal repair. If this were a burst pipe, gas smell, or immediate danger, I'd flag it for faster escalation per their plan.`;
  }
  if (
    /emergency|burst|flooding|fire|gas\s?smell|spark|no\s?heat|sewage/.test(t)
  ) {
    return `If there is immediate danger, call 911 first. For ${label}, Clumse AI can escalate true emergencies to your on-call contact by SMS or voice once we confirm severity. Tell me what you are seeing and when it started.`;
  }
  if (/rent|pay|payment|late|due|lease\s?break/.test(t)) {
    return `For payment timing I follow the rules your operator published for ${label}. I keep a verified transcript so everyone shares the same thread. What detail do you need?`;
  }
  if (/paint|pet|guest|noise|parking|sublet/.test(t)) {
    return `Policy questions like that usually come down to your site rules for ${label}. I can summarize the typical next step and your operator can confirm in the thread when they review it.`;
  }

  return `Thanks, I have that for ${label}. In a live thread, your operator gets this transcript in their dashboard instead of a scattered text chain. Anything else about this issue?`;
}

/**
 * Homepage Clumse AI demo: calls POST /api/public/leo-chat/marketing-demo when API URL is set;
 * otherwise deterministic offline replies. Transcript is saved in localStorage for the
 * current demo session so visitors can scroll back after hitting the message cap.
 */
export default function MarketingLeoDemoChat({
  propertyLabel,
  greetingText,
  /** When false, hides the caption above the phone (e.g. homepage already has a section title). */
  showDemoCaption = true,
}) {
  const label = useMemo(() => (propertyLabel || "").trim() || "this property", [
    propertyLabel,
  ]);

  const [sessionId, setSessionId] = useState(() =>
    typeof window !== "undefined" ? getDemoSession() : ""
  );

  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") {
      return API_BASE_URL ? "marketing" : "offline";
    }
    const saved = loadDemoTranscript(getDemoSession());
    if (saved?.mode === "marketing" || saved?.mode === "offline") {
      console.log("[DemoChat] restoring saved mode from transcript:", saved.mode);
      return saved.mode;
    }
    const initialMode = API_BASE_URL ? "marketing" : "offline";
    console.log("[DemoChat] initial mode:", initialMode, "| API_BASE_URL truthy:", !!API_BASE_URL);
    return initialMode;
  });

  const [offlineNote, setOfflineNote] = useState(null);

  const [messages, setMessages] = useState(() => {
    const applyGreeting = (bubble) =>
      greetingText ? { ...bubble, text: greetingText } : bubble;

    if (typeof window === "undefined") {
      return [
        applyGreeting(
          buildDemoIntroBubble(
            (propertyLabel || "").trim() || "this property",
            !!API_BASE_URL
          )
        ),
      ];
    }
    const sid = getDemoSession();
    const saved = loadDemoTranscript(sid);
    if (saved?.messages?.length) {
      return saved.messages;
    }
    const l = (propertyLabel || "").trim() || "this property";
    return [applyGreeting(buildDemoIntroBubble(l, !!API_BASE_URL))];
  });

  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  /** Mobile: fullscreen overlay so the demo matches Magic Link occupant chat (full viewport). */
  const [mobileImmersiveOpen, setMobileImmersiveOpen] = useState(false);
  const isMobileViewport = useMedia(device.mobile);
  const [isSending, setIsSending] = useState(false);
  const [demoLocked, setDemoLocked] = useState(false);
  const [msgCount, setMsgCount] = useState(() =>
    Math.min(getDemoMessageCount(), DEMO_USER_MESSAGE_CAP)
  );

  // Restore locked state from cookie on mount (persists across refreshes)
  useEffect(() => {
    const count = getDemoMessageCount();
    if (count >= DEMO_USER_MESSAGE_CAP) {
      setMsgCount(DEMO_USER_MESSAGE_CAP);
      setDemoLocked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  const messagesAreaRef = useRef(null);

  const closeMobileImmersive = useCallback(() => {
    setMobileImmersiveOpen(false);
  }, []);

  useEffect(() => {
    if (!mobileImmersiveOpen || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileImmersiveOpen]);

  useEffect(() => {
    if (!mobileImmersiveOpen || !isMobileViewport) return;
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
    return () => cancelAnimationFrame(id);
  }, [mobileImmersiveOpen, isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport) setMobileImmersiveOpen(false);
  }, [isMobileViewport]);

  useEffect(() => {
    if (!mobileImmersiveOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileImmersive();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileImmersiveOpen, closeMobileImmersive]);

  useEffect(() => {
    const area = messagesAreaRef.current;
    if (!area) return;
    requestAnimationFrame(() => {
      area.scrollTop = area.scrollHeight;
    });
  }, [messages, isSending]);

  useEffect(() => {
    if (!sessionId || messages.length === 0) return;
    saveDemoTranscript(sessionId, { messages, mode });
  }, [messages, mode, sessionId]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isSending || demoLocked) return;

    const priorUserCount = messages.filter((m) => m.from === "user").length;

    /* Cookie-enforced client-side limit (offline mode and as a fast-path before the API call) */
    const cookieCount = getDemoMessageCount();
    if (cookieCount >= DEMO_USER_MESSAGE_CAP) {
      setDemoLocked(true);
      setInputValue("");
      return;
    }

    /* Offline: show limit reply after the cap */
    if (mode === "offline" && priorUserCount >= DEMO_USER_MESSAGE_CAP) {
      const userMsg = {
        from: "user",
        text,
        timestamp: new Date().toISOString(),
      };
      trackHomeLeoDemoDiscord({
        sessionId,
        mode,
        propertyLabel: label,
        messageText: text,
        messageNumber: priorUserCount + 1,
      });
      const newCount = incrementDemoMessageCount();
      setMsgCount(Math.min(newCount, DEMO_USER_MESSAGE_CAP));
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          from: "assistant",
          text: DEMO_LIMIT_REPLY,
          timestamp: new Date().toISOString(),
        },
      ]);
      setInputValue("");
      setDemoLocked(true);
      return;
    }

    const historyBeforeSend = messages.map((m) => ({
      from: m.from,
      text: m.text,
      timestamp: m.timestamp,
    }));

    const userMsg = {
      from: "user",
      text,
      timestamp: new Date().toISOString(),
    };
    trackHomeLeoDemoDiscord({
      sessionId,
      mode,
      propertyLabel: label,
      messageText: text,
      messageNumber: priorUserCount + 1,
    });
    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsSending(true);
    const newCount = incrementDemoMessageCount();
    setMsgCount(Math.min(newCount, DEMO_USER_MESSAGE_CAP));

    console.log("[DemoChat] handleSend - mode:", mode, "| API_BASE_URL:", JSON.stringify(API_BASE_URL || "(empty)"), "| text:", JSON.stringify(text.slice(0, 60)));

    try {
      if (mode === "marketing" && API_BASE_URL) {
        const path = API_BASE_URL.endsWith("/api")
          ? "/public/leo-chat/marketing-demo"
          : "/api/public/leo-chat/marketing-demo";

        const fullUrl = `${API_BASE_URL}${path}`;
        console.log("[DemoChat] fetching:", fullUrl, "| sessionId:", sessionId, "| timeout:", MARKETING_DEMO_FETCH_MS + "ms");

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.warn("[DemoChat] AbortController fired; request exceeded", MARKETING_DEMO_FETCH_MS + "ms timeout");
          controller.abort();
        }, MARKETING_DEMO_FETCH_MS);

        let res;
        try {
          res = await fetch(fullUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sessionId,
              message: text.trim(),
              chatHistory: historyBeforeSend,
              propertyLabel: label,
            }),
            signal: controller.signal,
          });
          console.log("[DemoChat] fetch response:", res.status, res.statusText, "| ok:", res.ok);
        } finally {
          clearTimeout(timeoutId);
        }

        const rawText = await res.text();
        console.log("[DemoChat] raw response body:", rawText.slice(0, 300));
        let data;
        try {
          data = JSON.parse(rawText);
        } catch {
          data = {};
          console.error("[DemoChat] response was not valid JSON:", rawText.slice(0, 300));
        }

        if (!res.ok) {
          console.error("[DemoChat] non-OK response:", res.status, data);
          throw new Error(data.error || `Server error ${res.status}`);
        }

        const reply = data.reply || "I'm sorry, I could not reply. Try again.";
        console.log("[DemoChat] reply received:", reply.slice(0, 80), "| demoLimitReached:", data.demoLimitReached);
        if (data.demoLimitReached) {
          setDemoLocked(true);
        }

        setMessages((prev) => [
          ...prev,
          {
            from: "assistant",
            text: reply,
            timestamp: new Date().toISOString(),
          },
        ]);
      } else {
        console.log("[DemoChat] offline mode; serving deterministic reply");
        await new Promise((r) => setTimeout(r, 550 + Math.random() * 400));
        const reply = getOfflineLeoReply(text, label);
        setMessages((prev) => [
          ...prev,
          {
            from: "assistant",
            text: reply,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    } catch (err) {
      console.error("[DemoChat] send error; name:", err?.name, "| message:", err?.message, "| full:", err);
      const timedOut = err?.name === "AbortError";
      setMessages((prev) => [
        ...prev,
        {
          from: "assistant",
          text: timedOut
            ? "That took too long (network or server). Please try again."
            : "Something went wrong. Please try again in a moment.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  }, [inputValue, isSending, mode, messages, sessionId, label, demoLocked]);

  const onPressEnter = (e) => {
    if (e.shiftKey) return;
    e.preventDefault();
    e.stopPropagation();
    handleSend();
  };

  const openMobileImmersive = useCallback(() => {
    if (isMobileViewport) setMobileImmersiveOpen(true);
  }, [isMobileViewport]);

  const clearDemoRestrictions = useCallback(() => {
    if (typeof window === "undefined") return;
    clearDemoRestrictionStorage();
    const newSid = getDemoSession();
    setSessionId(newSid);
    setDemoLocked(false);
    setMsgCount(0);
    const resetBubble = buildDemoIntroBubble(label, !!API_BASE_URL);
    setMessages([
      greetingText ? { ...resetBubble, text: greetingText } : resetBubble,
    ]);
    setInputValue("");
    setOfflineNote(null);
  }, [label, greetingText]);

  const chatPanel = (
    <ChatCard $immersive={mobileImmersiveOpen && isMobileViewport}>
      <ChatHead>
        <HeaderLogoWrap>
          <HeaderLeoImg src="/icons/leo/leo.png" alt="" />
        </HeaderLogoWrap>
        <ChatHeadText>
          <ChatTitle>Chat with Clumse AI</ChatTitle>
          <ChatSub>
            {mode === "marketing"
              ? "Live AI demo"
              : "Offline demo · sample replies only"}
          </ChatSub>
        </ChatHeadText>
        <MsgCounter $exhausted={demoLocked} aria-live="polite">
          {msgCount}/{DEMO_USER_MESSAGE_CAP}
        </MsgCounter>
      </ChatHead>

      <DemoDisclaimer role="note">
        {`Demo only: no messages go to a real operator. Type reporter-style messages to preview the public chat surface.`}
      </DemoDisclaimer>

      <MessagesArea ref={messagesAreaRef}>
        {messages.map((msg, i) => (
          <MessageRow key={i} $user={msg.from === "user"}>
            {msg.from === "assistant" && (
              <AvatarWrap>
                <LeoAvatar src="/icons/leo/leo.png" alt="Clumse AI" />
              </AvatarWrap>
            )}
            <MsgContent $user={msg.from === "user"}>
              <MsgHeader $user={msg.from === "user"}>
                <MsgSender>
                  {msg.from === "user" ? "You" : "Clumse AI"}
                </MsgSender>
                <MsgTime>{formatDemoTime(msg.timestamp)}</MsgTime>
              </MsgHeader>
              <Bubble $user={msg.from === "user"}>{msg.text}</Bubble>
            </MsgContent>
          </MessageRow>
        ))}
        {isSending && (
          <MessageRow $user={false}>
            <AvatarWrap>
              <LeoAvatar src="/icons/leo/leo.png" alt="Clumse AI" />
            </AvatarWrap>
            <MsgContent $user={false}>
              <MsgHeader $user={false}>
                <MsgSender>Clumse AI</MsgSender>
              </MsgHeader>
              <TypingBubble>
                <TypingDots>
                  <span />
                  <span />
                  <span />
                </TypingDots>
              </TypingBubble>
            </MsgContent>
          </MessageRow>
        )}
      </MessagesArea>

      {demoLocked && (
        <LockedBanner role="status">
          <LockedIconWrap>
            <LockOutlined />
          </LockedIconWrap>
          <LockedText>
            <strong>Demo limit reached.</strong> Production workspaces apply your
            plan limits; Clumse AI still answers around the clock.
          </LockedText>
        </LockedBanner>
      )}

      <InputRow>
        <StyledInput
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => openMobileImmersive()}
          onPressEnter={onPressEnter}
          placeholder={
            demoLocked
              ? "Demo limit reached"
              : "Tenant-style message (e.g. leak, rent, noise)…"
          }
          disabled={isSending || demoLocked}
        />
        <SendBtn
          type="button"
          onClick={handleSend}
          disabled={!inputValue.trim() || isSending || demoLocked}
          aria-label="Send message"
        >
          <SendOutlined />
        </SendBtn>
      </InputRow>
      {SHOW_DEMO_DEBUG_CLEAR && (
        <DebugDemoRow>
          <DebugDemoBtn type="button" onClick={clearDemoRestrictions}>
            Clear demo limits (debug)
          </DebugDemoBtn>
        </DebugDemoRow>
      )}
    </ChatCard>
  );

  const immersivePortal =
    isMobileViewport &&
    mobileImmersiveOpen &&
    typeof document !== "undefined"
      ? createPortal(
          <MobileImmersiveRoot role="dialog" aria-modal="true" aria-label="Clumse AI public chat preview">
            <MobileImmersiveSheet>
              <ImmersiveChromeBar>
                <ImmersiveDoneBtn type="button" onClick={closeMobileImmersive}>
                  Done
                </ImmersiveDoneBtn>
                <ImmersiveChromeTitle>
                  Same full-screen chat your reporters see
                </ImmersiveChromeTitle>
                <ImmersiveChromeSpacer aria-hidden />
              </ImmersiveChromeBar>
              {chatPanel}
            </MobileImmersiveSheet>
          </MobileImmersiveRoot>,
          document.body
        )
      : null;

  return (
    <Wrap>
      {showDemoCaption && !(isMobileViewport && mobileImmersiveOpen) && (
        <DemoLabel>
          {mode === "marketing"
            ? "Try Clumse AI (live AI preview)"
            : "Try Clumse AI (public demo)"}
        </DemoLabel>
      )}
      {offlineNote && <ModeNote role="status">{offlineNote}</ModeNote>}

      {!(isMobileViewport && mobileImmersiveOpen) && chatPanel}
      {immersivePortal}
    </Wrap>
  );
}

const typingBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.7; }
  30% { transform: translateY(-6px); opacity: 1; }
`;

/** Assistant rows: avatar + gap before the bubble; user rows use the same value as right padding so edge margins match. */
const DEMO_ASSISTANT_AVATAR_PX = 24;
const DEMO_ASSISTANT_GAP = "0.35rem";
const DEMO_BUBBLE_EDGE_INSET = `calc(${DEMO_ASSISTANT_AVATAR_PX}px + ${DEMO_ASSISTANT_GAP})`;
/** Horizontal padding for the scroll area + composer (shared baseline). */
const DEMO_CHAT_GUTTER_X = "clamp(0.65rem, 2.8vw, 1rem)";
/** Extra space before user bubbles (left); keeps “You” from hugging the edge on mobile. */
const DEMO_USER_LEFT_INSET = "clamp(0.75rem, 4vw, 1.2rem)";
/** Extra space after AI bubbles (right); mirrors the user side visually. */
const DEMO_AI_RIGHT_INSET = "clamp(0.75rem, 4vw, 1.2rem)";

const Wrap = styled.div`
  width: 100%;
`;

const MobileImmersiveRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10020;
  animation: loDemoImmersiveIn 0.22s ease;

  @keyframes loDemoImmersiveIn {
    from {
      opacity: 0.92;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobileImmersiveSheet = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  overflow: hidden;
`;

const ImmersiveChromeBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.65rem;
  padding-top: max(0.5rem, env(safe-area-inset-top));
  background: #ffffff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
`;

const ImmersiveDoneBtn = styled.button`
  border: none;
  background: transparent;
  color: var(--color-primary, #2c5f2d);
  font-weight: 600;
  font-size: 1rem;
  padding: 0.35rem 0.45rem;
  margin: 0;
  cursor: pointer;
  font-family: var(--font-secondary, sans-serif);
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.75;
  }
`;

const ImmersiveChromeTitle = styled.span`
  flex: 1;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  font-family: var(--font-primary, sans-serif);
  line-height: 1.25;
`;

const ImmersiveChromeSpacer = styled.span`
  width: 52px;
  flex-shrink: 0;
`;

const DemoLabel = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(240, 253, 244, 0.45);
  margin: 0 0 0.65rem 0;
  font-family: var(--font-primary, sans-serif);
`;

const ModeNote = styled.p`
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(250, 204, 21, 0.85);
  margin: 0 0 0.65rem 0;
  font-family: var(--font-secondary, sans-serif);
`;

const ChatCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 28px;
  overflow: hidden;
  height: min(640px, 76vh);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);

  @media (max-width: 767px) {
    height: min(600px, 80vh);
    border-radius: 24px;
  }
`;

const ChatHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(44, 95, 45, 0.1);
  background: linear-gradient(
    135deg,
    var(--color-primary, #2c5f2d) 0%,
    var(--color-primary-dark, #1e4d1e) 100%
  );
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);
  flex-shrink: 0;
`;

const HeaderLogoWrap = styled.div`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeaderLeoImg = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const ChatHeadText = styled.div`
  min-width: 0;
  flex: 1;
`;

const MsgCounter = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: var(--font-primary, sans-serif);
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$exhausted ? "rgba(239, 68, 68, 0.25)" : "rgba(255, 255, 255, 0.2)"};
  border: 1px solid
    ${(p) =>
      p.$exhausted ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.35)"};
  color: ${(p) =>
    p.$exhausted ? "rgba(254, 202, 202, 1)" : "rgba(255, 255, 255, 0.95)"};
  transition: background 0.25s, border-color 0.25s, color 0.25s;
`;

const ChatTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  font-family: var(--font-primary, sans-serif);
  text-align: left;
`;

const ChatSub = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: var(--font-secondary, sans-serif);
  text-align: left;
`;

const DemoDisclaimer = styled.p`
  margin: 0;
  padding: 0.5rem 1rem 0.55rem;
  font-size: 0.68rem;
  line-height: 1.45;
  color: #475569;
  background: rgba(241, 245, 249, 0.95);
  border-bottom: 1px solid rgba(44, 95, 45, 0.08);
  font-family: var(--font-secondary, sans-serif);
  flex-shrink: 0;
`;

const MessagesArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem ${DEMO_CHAT_GUTTER_X};
  /* User bubble right inset = Leo bubble left inset (avatar column) */
  --demo-bubble-edge-inset: ${DEMO_BUBBLE_EDGE_INSET};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(241, 241, 241, 0.5);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-primary, #2c5f2d);
    border-radius: 3px;
  }
`;

const MessageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${(p) => (p.$user ? "0.4rem" : DEMO_ASSISTANT_GAP)};
  width: 100%;
  box-sizing: border-box;
  /* User: extra left inset + right pad aligned to Leo’s avatar column; AI: extra right inset */
  padding: ${(p) =>
    p.$user
      ? `0 var(--demo-bubble-edge-inset) 0 ${DEMO_USER_LEFT_INSET}`
      : `0 ${DEMO_AI_RIGHT_INSET} 0 0`};
  flex-direction: ${(p) => (p.$user ? "row-reverse" : "row")};
  justify-content: ${(p) => (p.$user ? "flex-end" : "flex-start")};

  @media ${device.mobile} {
    ${(p) =>
      p.$user &&
      `
      flex-direction: row;
      justify-content: flex-start;
      padding: 0 ${DEMO_AI_RIGHT_INSET} 0 ${DEMO_CHAT_GUTTER_X};
    `}
  }
`;

const AvatarWrap = styled.div`
  flex-shrink: 0;
`;

const LeoAvatar = styled.img`
  width: ${DEMO_ASSISTANT_AVATAR_PX}px;
  height: ${DEMO_ASSISTANT_AVATAR_PX}px;
  border-radius: 50%;
  object-fit: contain;
  background: var(--color-primary, #2c5f2d);
  padding: 3px;
  filter: brightness(0) invert(1);
`;

const MsgContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$user ? "flex-end" : "flex-start")};
  flex: 1;
  min-width: 0;
  /* 100%: avoid a phantom column gap (old 94% cap left empty space beside user bubbles) */
  max-width: 100%;

  @media ${device.mobile} {
    ${(p) => p.$user && `align-items: flex-start;`}
  }
`;

/** Row holding sender name + timestamp; matches TenantChat's MessageHeader */
const MsgHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.25rem;
  flex-direction: ${(p) => (p.$user ? "row-reverse" : "row")};
  justify-content: ${(p) => (p.$user ? "flex-end" : "flex-start")};

  @media ${device.mobile} {
    ${(p) =>
      p.$user &&
      `
      flex-direction: row;
      justify-content: flex-start;
    `}
  }
`;

const MsgSender = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  font-family: var(--font-secondary, sans-serif);
`;

const MsgTime = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
  font-family: var(--font-secondary, sans-serif);
`;

const Bubble = styled.div`
  padding: ${(p) => (p.$user ? "0.7rem 0.85rem" : "0.7rem 0.75rem")};
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.65;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  font-family: var(--font-secondary, sans-serif);
  text-align: left;
  box-sizing: border-box;

  ${(p) =>
    p.$user
      ? `
    width: fit-content;
    max-width: 100%;
    color: #ffffff;
    background: var(--color-primary, #2c5f2d);
    border: 1px solid var(--color-primary, #2c5f2d);
    box-shadow: 0 2px 4px rgba(44, 95, 45, 0.2);
    align-self: flex-end;

    @media ${device.mobile} {
      align-self: stretch;
      width: 100%;
    }
  `
      : `
    width: 100%;
    max-width: 100%;
    color: #1f2937;
    background: rgba(44, 95, 45, 0.05);
    border: 1px solid rgba(44, 95, 45, 0.1);
    box-shadow: 0 2px 4px rgba(44, 95, 45, 0.08);
    align-self: stretch;
  `}
`;

const TypingBubble = styled.div`
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0.7rem 0.75rem;
  border-radius: 12px;
  background: rgba(44, 95, 45, 0.05);
  border: 1px solid rgba(44, 95, 45, 0.1);
  box-shadow: 0 2px 4px rgba(44, 95, 45, 0.08);
  display: flex;
  align-items: center;
  align-self: flex-start;
  flex-shrink: 0;
`;

const TypingDots = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 0.1rem 0;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-primary, #2c5f2d);
    animation: ${typingBounce} 1.4s ease-in-out infinite;
    opacity: 0.7;
  }
  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

const LockedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.9rem;
  background: rgba(239, 68, 68, 0.07);
  border-top: 1px solid rgba(239, 68, 68, 0.2);
  flex-shrink: 0;
`;

const LockedIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #dc2626;
  font-size: 0.75rem;
`;

const LockedText = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #b91c1c;
  font-family: var(--font-secondary, sans-serif);

  strong {
    color: #991b1b;
    font-weight: 700;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.75rem ${DEMO_CHAT_GUTTER_X};
  padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  border-top: 1px solid rgba(44, 95, 45, 0.1);
  flex-shrink: 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
`;

const DebugDemoRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0.35rem ${DEMO_CHAT_GUTTER_X} 0.65rem;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
`;

const DebugDemoBtn = styled.button`
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(71, 85, 105, 0.85);
  background: transparent;
  border: 1px dashed rgba(100, 116, 139, 0.45);
  border-radius: 6px;
  padding: 0.35rem 0.55rem;
  cursor: pointer;
  font-family: var(--font-secondary, sans-serif);

  &:hover {
    color: #2c5f2d;
    border-color: rgba(44, 95, 45, 0.45);
    background: rgba(44, 95, 45, 0.06);
  }
`;

const StyledInput = styled(Input)`
  flex: 1;
  min-width: 0;
  height: 40px !important;

  &&,
  &&.ant-input-outlined {
    height: 40px !important;
    background: #ffffff !important;
    border-color: #d1d5db !important;
    border-radius: 10px !important;
    font-size: 0.9rem !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
    color: #1f2937 !important;
    -webkit-text-fill-color: #1f2937 !important;
    caret-color: var(--color-primary, #2c5f2d) !important;
  }

  &&::placeholder,
  &&.ant-input-outlined::placeholder {
    color: #9ca3af !important;
    -webkit-text-fill-color: #9ca3af !important;
  }

  &&:hover,
  &&.ant-input-outlined:hover {
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  &&:focus,
  &&.ant-input-outlined:focus {
    border-color: var(--color-primary, #2c5f2d) !important;
    box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
  }
`;

const SendBtn = styled(Button)`
  && {
    height: 40px;
    width: 44px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-primary, #2c5f2d);
    border: none;
    border-radius: 10px;
    color: #ffffff;
    flex-shrink: 0;
  }
  && .anticon {
    filter: brightness(0) invert(1);
  }
  &&:hover:not(:disabled) {
    background: var(--color-primary-dark, #1e4d1e) !important;
    color: #ffffff !important;
  }
  &&:disabled {
    background: #d1d5db !important;
    border-color: #d1d5db !important;
    color: #9ca3af !important;
    opacity: 0.6 !important;
  }
`;
